<template>
  <div class="product-price" :class="rootClasses">
    <div class="product-price__prices">
      <template v-if="! isHydrated">
        <div
          v-if="hasOldPrice"
          class="product-price__old-price product-price__old-price--hidden"
          v-html="formattedPrice(showExTax ? defaultCurrency?.regularPriceExTax : defaultCurrency?.regularPrice)" />
        <div
          class="product-price__current-price product-price__current-price--hidden"
          v-html="formattedPrice(showExTax ? defaultCurrency?.priceExTax : defaultCurrency?.price)" />
      </template>
      <template v-else>
        <div
          v-if="hasOldPrice"
          class="product-price__old-price"
          v-html="oldPrice" />
        <div
          class="product-price__current-price"
          v-html="currentPrice" />
      </template>
    </div>
    <small v-if="taxIndicatorText" class="product-price__tax-indicator">{{ taxIndicatorText }}</small>
  </div>
</template>

<script lang="ts">
import { useUserStore } from "~/stores/user";
import { usePriceStore } from "~/stores/priceStore";

export default {
  props: {
    defaultCurrency: {
      type: Object,
      required: true,
    },
    isFromPrice: Boolean,
    differentSizes: Boolean,
    productId: [String, Number],
    showTaxIndicator: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const userStore = useUserStore();

    return {
      userStore,
      priceStore: usePriceStore(),
    }
  },
  watch: {
    userCountry (newValue, oldValue) {
      this.fetchPrices(true);
    },
    productId() {
      this.fetchPrices();
    }
  },
  mounted() {
    if (! this.isFetchPriceRequired) {
      this.isHydrated = true;
      return
    }
    this.fetchPrices();
  },
  data() {
    return {
      isHydrated: false,
      priceMap: {
        current: this.defaultCurrency?.price || '',
        old: this.defaultCurrency?.regularPrice || '',
        sale: this.defaultCurrency?.salePrice || '',
        currentExTax: this.defaultCurrency?.priceExTax || '',
        oldExTax: this.defaultCurrency?.regularPriceExTax || '',
        saleExTax: this.defaultCurrency?.salePriceExTax || '',
      }
    }
  },
  computed: {
    userCountry () {
      // update when user cookie changes
      return this.userStore.getCountryCookie;
    },
    isFetchPriceRequired () {
      const isFetchPriceRequired = this.userCurrency !== 'USD';

      return isFetchPriceRequired;
    },
    showExTax () {
      return this.userStore.showExTax;
    },
    userCurrency () {
      return this.userStore.getSelectedCurrency;
    },
    hasOldPrice (): boolean {
      return this.priceMap.sale && this.priceMap.old !== this.priceMap.current;
    },
    rootClasses (): Record<string, boolean> {
      return {
        'product-price--different-sizes': this.differentSizes,
        'product-price--hidden': !this.isHydrated,
      };
    },
    currentPrice () {
      return this.formattedPrice(this.showExTax ? this.priceMap.currentExTax : this.priceMap.current)
    },
    oldPrice () {
      return this.formattedPrice(this.showExTax ? this.priceMap.oldExTax : this.priceMap.old)
    },
    taxIndicatorText (): string {
      if (!this.showTaxIndicator) {
        return '';
      }

      // If VAT exempt (B2B with valid VAT number)
      if (this.showExTax) {
        return 'excl. VAT';
      }

      // If not VAT exempt and using EUR currency (EU countries with VAT)
      // USD and other currencies are for third countries with no VAT
      if (!this.showExTax && this.userCurrency === 'EUR') {
        return 'incl. VAT';
      }

      // Third country (no VAT) - don't show anything
      return '';
    },
  },
  methods: {
    formattedPrice (priceString): string {
      return this.isFromPrice
        ? `from ${priceString}`
        : priceString;
    },
    fetchPrices (refresh = false) {
      if (this.productId) {
        this.priceStore.getPrice(this.productId, refresh).then((priceMap) => {
          this.priceMap = priceMap;
        }).finally(() => {
          // Ensure that there will be a price visible, even if it might not be correct.
          this.isHydrated = true;
        })
      } else {
        this.isHydrated = true;
      }
    },
  },
};
</script>

<style lang="scss">
@use "~/common/scss/helpers";

.product-price {
  $root: &;

  --local-price-font-size: var(--price-font-size, #{helpers.px-to-rem(30)});

  display: flex;
  flex-direction: column;
  font-size: var(--local-price-font-size);
  line-height: 1;
  opacity: 1;
  transition: opacity .25s;

  &--hidden {
    opacity: 0;
  }

  &--different-sizes {
    font-size: calc(var(--local-price-font-size) * .7);
  }

  &__prices {
    display: flex;
    flex-direction: column;
  }

  &__current-price {
    font-size: var(--local-price-font-size);
    font-weight: bold;
    line-height: 1;
    margin-top: .25rem;
  }

  &__old-price {
    text-decoration: line-through;
    line-height: 1;
    margin-top: .15rem;
  }

  &__tax-indicator {
    font-size: calc(var(--local-price-font-size) * 0.4);
    font-weight: normal;
    color: #666;
    margin-top: helpers.px-to-rem(2);
  }
}
</style>
